import type { FurniturePart, Material, PartRole } from "../types";
import { shade, withAlpha } from "./color";

/**
 * Top-down shading model. One soft light from the top-left; taller parts read
 * a touch lighter (they're closer to the light and the eye), lower parts a
 * touch darker; a handful of roles override the base tint entirely (pillows
 * and mattresses read as linen, screens as black glass).
 *
 * Returned values are plain colors — the material painter (Phase 2) layers
 * texture on top of these.
 */

/** Per-role lightness bias (mixed toward white when > 0, black when < 0). */
const ROLE_BIAS: Record<PartRole, number> = {
  body: -0.16,
  seat: 0.09,
  chaise: 0.09,
  backrest: -0.03,
  arm: 0.02,
  cushion: 0.17,
  pillow: 0.58,
  mattress: 0.55,
  blanket: 0.0,
  headboard: -0.12,
  top: 0.05,
  leg: -0.25,
  "drawer-face": -0.12,
  "door-face": -0.12,
  shelf: 0.1,
  screen: 0,
  base: -0.15,
  pot: 0,
  foliage: 0.02,
  shade: 0.62,
  detail: -0.18,
};

export interface PartPaint {
  /** Fill color (hex or rgba). */
  fill: string;
  /** Edge stroke color. */
  stroke: string;
  /** Seam stroke color. */
  seam: string;
  /** Opacity for translucent parts. */
  opacity: number;
  /** How much of the material texture to show (0 = none). */
  textureStrength: number;
  /** Shadow blur in inches this part casts onto parts below it (0 = none). */
  castShadowIn: number;
}

/** Resolve the effective material for a part given the piece's material. */
export function partMaterial(p: FurniturePart, base: Material): Material {
  return {
    id: p.material?.id ?? base.id,
    tint: p.material?.tint ?? base.tint,
  };
}

/**
 * Compute the flat paint for a part. `maxZ` is the tallest part top in the
 * piece so relative height can bias the tint.
 */
export function paintFor(p: FurniturePart, base: Material, maxZ: number): PartPaint {
  const mat = partMaterial(p, base);
  const roleBias = ROLE_BIAS[p.role] ?? 0;
  // Relative height: -0.04 (floor) … +0.04 (tallest).
  const rel = maxZ > 0 ? (p.z1 / maxZ - 0.5) * 0.08 : 0;
  let fill = shade(mat.tint, roleBias + rel);
  let stroke = withAlpha(shade(mat.tint, -0.5), 0.42);
  let opacity = 1;
  let textureStrength = 1;
  switch (p.role) {
    case "pillow":
    case "mattress":
      // Linen: pull strongly toward warm white regardless of upholstery tint.
      fill = shade(mat.tint, roleBias);
      stroke = withAlpha(shade(mat.tint, -0.3), 0.3);
      textureStrength = 0.35;
      break;
    case "shade":
      opacity = 0.82;
      textureStrength = 0.2;
      break;
    case "screen":
      fill = p.material?.tint ?? "#15151a";
      stroke = "rgba(0,0,0,0.5)";
      textureStrength = 0;
      break;
    case "leg":
    case "base":
      textureStrength = 0.4;
      break;
    case "detail":
      textureStrength = 0.2;
      break;
    default:
      break;
  }
  if (p.translucent) {
    opacity = Math.min(opacity, 0.5);
    textureStrength = 0;
    stroke = withAlpha(shade(mat.tint, -0.3), 0.25);
  }
  if (mat.id === "glass" && (p.role === "top" || p.role === "body")) {
    opacity = 0.55;
    fill = shade("#b9dbe4", 0.15);
    stroke = withAlpha("#4a7f8f", 0.5);
    textureStrength = 0.15;
  }
  const seam = withAlpha(shade(mat.tint, -0.5), 0.35);
  // Taller-than-neighbours parts cast a small shadow (backrests onto seats).
  const castShadowIn =
    p.role === "backrest" || p.role === "arm" || p.role === "headboard" || p.role === "cushion" || p.role === "pillow"
      ? 1.2
      : p.role === "foliage"
        ? 2
        : 0;
  return { fill, stroke, seam, opacity, textureStrength, castShadowIn };
}

/** Sort parts bottom-to-top so higher parts paint over lower ones. */
export function sortPartsForPaint(parts: FurniturePart[]): FurniturePart[] {
  return parts
    .map((p, i) => ({ p, i }))
    .sort((a, b) => a.p.z1 - b.p.z1 || a.p.z0 - b.p.z0 || a.i - b.i)
    .map((x) => x.p);
}
